'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

export default function TestimonialsSection() {
  const testimonials = [
    {
      id: 1,
      name: 'Abhi & Niyu',
      position: 'Youtube Content Creators',
      quote: 'I attended the workshop & I loved it and would give it 5/5 stars.',
      largeImage: '/images/ic2.webp',
      thumbnailImage: '/images/icc2.png',
    },
    {
      id: 2,
      name: 'Ankur Warikoo',
      position: 'Entrepreneur, Content Creator & Author',
      quote: 'I know Vaibhav as the guy who is killing it on LinkedIn, because of everything he is doing and he understands the platform so well.',
      largeImage: '/images/ic4.webp',
      thumbnailImage: '/images/icc4.png',
    },
    {
      id: 3,
      name: 'Tanmay Bhat',
      position: 'Youtuber, Comedian & Investor',
      quote: 'I heard if I do something with you I will get 500,000 followers on LinkedIn the next day.',
      largeImage: '/images/ic1.webp',
      thumbnailImage: '/images/icc1.png',
    },
    {
      id: 4,
      name: 'Raj Shamani',
      position: 'Entrepreneur, Podcast Host & Author',
      quote: "Vaibhav understands growth, it's like it's in his DNA. Reach, engagement, scale - you name it & he has a growth hack for everything.",
      largeImage: '/images/ic3.webp',
      thumbnailImage: '/images/icc3.png',
    },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbnailIndices, setThumbnailIndices] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoplayRef = useRef(null);
  const resetTransitionRef = useRef(null);

  // Update thumbnail indices when activeIndex changes
  useEffect(() => {
    const indices = [];
    for (let i = 1; i <= 3; i++) {
      indices.push((activeIndex + i) % testimonials.length);
    }
    setThumbnailIndices(indices);
  }, [activeIndex, testimonials.length]);

  // Navigate to a specific testimonial
  const goToTestimonial = (index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    resetTransitionRef.current = setTimeout(() => {
      setActiveIndex(index);
      setIsTransitioning(false);
    }, 500);
  };

  // Auto-rotate every 5s
  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      const nextIndex = (activeIndex + 1) % testimonials.length;
      goToTestimonial(nextIndex);
    }, 5000);

    return () => {
      clearInterval(autoplayRef.current);
      clearTimeout(resetTransitionRef.current);
    };
  }, [activeIndex, testimonials.length]);

  return (
    <section className="w-full py-16 bg-white">
      <div className="w-[90%] md:w-[80%] mx-auto">
        <div className="flex flex-col md:flex-row md:items-start gap-8">
          {/* Left side - Section title */}
          <div className="md:w-[40%]">
            <p className="text-sm md:text-base uppercase font-medium tracking-wide text-gray-600 mb-4">
              GLOBALLY ACCLAIMED
            </p>
            <h2 className="text-3xl md:text-[36px] font-medium leading-tight">
              Recommended by your Favourite Influencers and Creators
            </h2>
          </div>

          {/* Right side - Testimonial card */}
          <div className="md:w-[65%]">
            <div className="relative rounded-2xl bg-[#002726] overflow-hidden ">
              {/* Mobile: Text at top, then bottom section with image left and thumbnails right */}
              <div className="flex flex-col md:hidden">
                {/* Text content - top on mobile */}
                <div className="p-4 pt-6 relative">
                  <div className="relative min-h-[170px] mb-6">
                    {testimonials.map((t, i) => (
                      <div
                        key={t.id}
                        className={`absolute inset-0 transition-opacity duration-500 ${
                          activeIndex === i && !isTransitioning
                            ? 'opacity-100 z-10'
                            : 'opacity-0 z-0'
                        }`}
                      >
                        <p className="text-white text-lg mb-6 relative">
                          {t.quote}
                        </p>
                        <div>
                          <h3 className="text-white text-base font-medium mb-2">
                            {t.name}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {t.position}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bottom section - image left, thumbnails right on mobile */}
                <div className="flex items-end justify-between">
                  {/* Large image - bottom left on mobile */}
                  <div className="flex justify-start">
                    <div className="relative w-[180px]">
                      <div className="relative w-full h-[170px] transition-opacity duration-500">
                        {testimonials.map((t, i) => (
                          <div
                            key={t.id}
                            className={`absolute inset-0 transition-opacity duration-500 ${
                              activeIndex === i && !isTransitioning
                                ? 'opacity-100 z-10'
                                : 'opacity-0 z-0'
                            }`}
                          >
                            <Image
                              src={t.largeImage}
                              alt={t.name}
                              fill
                              sizes="140px"
                              className="object-cover object-bottom rounded-l-2xl"
                              priority={i === 0}
                              loading={i === 0 ? "eager" : "lazy"}
                              unoptimized
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Thumbnails - bottom right on mobile */}
                  <div className="flex items-center justify-end space-x-3 pb-4 md-pb-0 pr-2 md:pr-0">
                    {thumbnailIndices.map((idx) => (
                      <button
                        key={`thumb-${testimonials[idx].id}`}
                        onClick={() => goToTestimonial(idx)}
                        className="w-10 h-10 relative rounded-full overflow-hidden border border-transparent hover:scale-150 transition-all flex-shrink-0"
                      >
                        <div className="absolute inset-0" style={{ transform: 'scale(0.9)' }}>
                          <Image
                            src={testimonials[idx].thumbnailImage}
                            alt={testimonials[idx].name}
                            fill
                            sizes="40px"
                            className="object-cover object-center"
                            loading="lazy"
                            unoptimized
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Desktop: Original layout - DO NOT CHANGE */}
              <div className="hidden md:flex md:flex-row md:items-end relative">
                {/* Large portrait image - left on desktop */}
                <div className="md:w-[32%] flex md:justify-start">
                  <div className="relative w-full">
                    <div className="relative w-full h-[250px] transition-opacity duration-500">
                      {testimonials.map((t, i) => (
                        <div
                          key={t.id}
                          className={`absolute inset-0 transition-opacity duration-500 ${
                            activeIndex === i && !isTransitioning
                              ? 'opacity-100 z-10'
                              : 'opacity-0 z-0'
                          }`}
                        >
                          <Image
                            src={t.largeImage}
                            alt={t.name}
                            fill
                            sizes="220px"
                            className="object-cover object-bottom rounded-l-2xl md:ml-4"
                            priority={i === 0}
                            loading={i === 0 ? "eager" : "lazy"}
                            unoptimized
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content - right on desktop */}
                <div className="md:w-[68%] flex flex-col justify-between p-4 md:p-6 md:pb-6 relative">
                  {/* Quote with opening quotation mark */}
                  <div className="relative min-h-[170px] mb-6">
                    {testimonials.map((t, i) => (
                      <div
                        key={t.id}
                        className={`absolute inset-0 transition-opacity duration-500 ml-2 mt-2 ${
                          activeIndex === i && !isTransitioning
                            ? 'opacity-100 z-10'
                            : 'opacity-0 z-0'
                        }`}
                      >
                        <p className="text-white text-lg md:text-xl mb-6 relative">
                          {t.quote}
                        </p>
                        <div>
                          <h3 className="text-white text-base md:text-xl font-medium mb-2">
                            {t.name}
                          </h3>
                          <p className="text-gray-400 text-sm md:text-base">
                            {t.position}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Thumbnail navigation - bottom right */}
                  <div className="flex items-center justify-end space-x-3 md:space-x-4">
                    {thumbnailIndices.map((idx) => (
                      <button
                        key={`thumb-${testimonials[idx].id}`}
                        onClick={() => goToTestimonial(idx)}
                        className="w-10 h-10 md:w-12 md:h-12 relative rounded-full overflow-hidden border border-transparent hover:scale-150 transition-all flex-shrink-0"
                      >
                        <div className="absolute inset-0" style={{ transform: 'scale(0.9)' }}>
                          <Image
                            src={testimonials[idx].thumbnailImage}
                            alt={testimonials[idx].name}
                            fill
                            sizes="48px"
                            className="object-cover object-center"
                            loading="lazy"
                            unoptimized
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}